import { useState } from "react";
import { useTasks, type Task, type TaskStatus, type TaskPriority, type TaskComplexity, type TaskStage } from "../store/tasks";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { DEPARTMENTS, PERSONAS, ALL_DEPTS } from "../data/org";
import { profileOf } from "../data/profiles";
import { initials, monogramColor } from "../lib/monogram";
import {
  generateImageGemini, generateImageOpenAI, generateVideoHiggsfield,
  capabilitiesOf
} from "../lib/generators";
import { suggestAgentsAI, estimateComplexityAI, modelForComplexity, autoExecuteByAgentAI, mediateConflictAI, tiebreakerAI, improveImagePromptAI } from "../lib/claude";
import { creativeAdPrompt } from "../lib/promptTemplates";
import { DeckGenerator } from "./DeckGenerator";
import { MarkdownView } from "./MarkdownView";
import { PipelineRunner } from "./PipelineRunner";
import { useDeckJobs } from "../store/deckJobs";
import { CLIENTS, clientById } from "../data/clients";
import { useCustomOrg } from "../store/customOrg";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { useUI } from "../store/ui";

const STATUSES: TaskStatus[] = ["pending", "in-progress", "review", "done", "blocked"];
const STATUS_LABEL: Record<TaskStatus, string> = {
  pending:       "PENDENTE",
  "in-progress": "EM EXECUÇÃO",
  review:        "EM REVISÃO",
  done:          "ENTREGUE",
  blocked:       "BLOQUEADA"
};
const STATUS_COLOR: Record<TaskStatus, string> = {
  pending:       "bg-graydk text-bone",
  "in-progress": "bg-ink text-bone",
  review:        "bg-gold text-ink",
  done:          "bg-red text-bone",
  blocked:       "bg-paper text-bone"
};

const PRIORITY_COLOR: Record<TaskPriority, string> = {
  low:    "bg-graymd",
  normal: "bg-ink",
  high:   "bg-gold",
  urgent: "bg-red"
};

function fmt(ts: number) {
  return new Date(ts).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
  });
}

export function DemandsBoard() {
  const tasks       = useTasks(s => s.tasks);
  const setStatus   = useTasks(s => s.setStatus);
  const remove      = useTasks(s => s.remove);
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId]   = useState<string | null>(null);

  const byStatus: Record<TaskStatus, Task[]> = {
    pending: [], "in-progress": [], review: [], done: [], blocked: []
  };
  tasks.forEach(t => byStatus[t.status].push(t));

  return (
    <div className="px-8 pb-16">
      <header className="border-2 border-ink bg-ink text-bone px-5 py-4 mb-4 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-gold">DEM-01</div>
          <h2 className="font-display font-black uppercase text-4xl leading-none">QUADRO DE DEMANDAS</h2>
          <div className="font-serif italic mt-1 text-bone/70">
            {tasks.length} demanda{tasks.length === 1 ? "" : "s"} no pipeline ·{" "}
            {byStatus.pending.length} pendente · {byStatus["in-progress"].length} em execução
          </div>
        </div>
        <button onClick={() => setShowNew(true)} className="btn bg-bone text-ink border-bone">+ nova demanda</button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {STATUSES.map(st => (
          <div key={st} className="border-2 border-ink bg-bone">
            <div className={`${STATUS_COLOR[st]} px-3 py-2 flex items-center justify-between`}>
              <span className="font-display font-bold uppercase text-sm">{STATUS_LABEL[st]}</span>
              <span className="tag">{byStatus[st].length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[200px]">
              {byStatus[st].map(t => (
                <TaskCard
                  key={t.id}
                  task={t}
                  onOpen={() => setOpenId(t.id)}
                  onAdvance={() => {
                    const idx = STATUSES.indexOf(t.status);
                    if (idx >= 0 && idx < STATUSES.length - 2) setStatus(t.id, STATUSES[idx + 1]);
                    else if (t.status !== "done") setStatus(t.id, "done");
                  }}
                  onDelete={() => { if (confirm("Apagar esta demanda?")) remove(t.id); }}
                />
              ))}
              {byStatus[st].length === 0 && (
                <div className="text-center py-6 font-crimson italic text-graymd text-sm">vazio</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showNew && <NewDemandModal onClose={() => setShowNew(false)} />}
      {openId && <DemandDetailModal taskId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

const COMPLEXITY_BADGE: Record<string, string> = {
  trivial:  "bg-graymd text-ink",
  low:      "bg-graydk text-bone",
  medium:   "bg-ink text-bone",
  high:     "bg-gold text-ink",
  critical: "bg-red text-bone"
};

const STAGE_LABEL: Record<TaskStage, string> = {
  draft:               "RASCUNHO",
  delegated:           "DELEGADA",
  "in-progress":       "EM EXECUÇÃO",
  "supervisor-review": "REVISÃO SUP.",
  "client-approval":   "AGUARDA MARCIO",
  delivered:           "ENTREGUE",
  blocked:             "BLOQUEADA",
  rejected:            "REJEITADA"
};

const STAGE_COLOR: Record<TaskStage, string> = {
  draft:               "bg-graymd text-ink",
  delegated:           "bg-graydk text-bone",
  "in-progress":       "bg-ink text-bone",
  "supervisor-review": "bg-gold text-ink",
  "client-approval":   "bg-red text-bone",
  delivered:           "bg-ink text-bone",
  blocked:             "bg-paper text-bone",
  rejected:            "bg-red text-bone"
};

function TaskCard({ task, onOpen, onAdvance, onDelete }:
  { task: Task; onOpen: () => void; onAdvance: () => void; onDelete: () => void }) {
  const job = useDeckJobs(s => s.jobs[task.id]);
  const isJobActive = !!job && (job.status === "generating" || job.status === "critiquing" || job.status === "refining");

  return (
    <div className={`relative border-2 ${isJobActive ? "border-red shadow-[3px_3px_0_0_#BC2A24]" : "border-ink"} bg-bone p-2`}>
      {isJobActive && (
        <div className="absolute -top-1 -right-1 bg-red text-bone text-[9px] px-1 font-mono uppercase tracking-wider">
          {job.status === "generating" ? "🔄 gerando" : job.status === "critiquing" ? "🔍 Paula" : "🔧 refinando"}
        </div>
      )}
      <div className="flex items-start justify-between gap-1 mb-1">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`inline-block w-3 h-3 ${PRIORITY_COLOR[task.priority]}`} title={`prioridade: ${task.priority}`} />
          {task.complexity && (
            <span className={`tag px-1 ${COMPLEXITY_BADGE[task.complexity]}`}
                  title="complexidade">
              {task.complexity}
            </span>
          )}
          {task.stage && (
            <span className={`tag px-1 ${STAGE_COLOR[task.stage]}`} title="estágio">
              {STAGE_LABEL[task.stage]}
            </span>
          )}
          {task.estimatedHours && (
            <span className="tag bg-bone border border-ink px-1" title="estimativa">
              {task.estimatedHours}h
            </span>
          )}
        </div>
        <span className="tag text-graydk">{fmt(task.createdAt)}</span>
      </div>
      <button onClick={onOpen} className="text-left w-full">
        <div className="font-display font-bold uppercase text-base leading-tight line-clamp-2 hover:underline">
          {task.title}
        </div>
      </button>
      <div className="font-crimson italic text-paper text-[12px] line-clamp-2 mt-1">
        {task.description}
      </div>
      <div className="flex flex-wrap gap-1 mt-2 items-center">
        {task.clientId && (
          <span className="tag px-1.5"
            style={{ background: clientById(task.clientId)?.brand.primary ?? "#121214", color: "#F2EEE6" }}>
            ▣ {clientById(task.clientId)?.shortName}
          </span>
        )}
        {task.targetDepts.slice(0, 3).map(d => (
          <span key={d} className="tag bg-ink text-bone px-1.5">
            {ALL_DEPTS.find(x => x.id === d)?.name ?? d}
          </span>
        ))}
      </div>
      {task.assignedTo.length > 0 && (
        <div className="flex -space-x-1 mt-2">
          {task.assignedTo.slice(0, 5).map(id => {
            const p = PERSONAS.find(x => x.id === id);
            if (!p) return null;
            return (
              <span key={id}
                className="w-6 h-6 border border-bone flex items-center justify-center
                           font-display font-black text-[10px] text-bone"
                style={{ background: monogramColor(p.id) }} title={p.name}>
                {initials(p.name)}
              </span>
            );
          })}
        </div>
      )}
      <div className="flex gap-1 mt-2 justify-end">
        <button onClick={onDelete} className="tag text-red underline">apagar</button>
        {task.status !== "done" && (
          <button onClick={onAdvance} className="tag underline">avançar →</button>
        )}
      </div>
    </div>
  );
}

/* -------------------- NEW DEMAND MODAL -------------------- */
function NewDemandModal({ onClose }: { onClose: () => void }) {
  const create   = useTasks(s => s.create);
  const employees= useRH(s => s.employees);
  const settings = useSettings();

  const [title, setTitle] = useState("");
  const [desc, setDesc]   = useState("");
  const [clientId, setClientId] = useState("");
  const [depts, setDepts] = useState<string[]>([]);
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [complexity, setComplexity] = useState<TaskComplexity>("medium");
  const [complexityReason, setComplexityReason] = useState("");
  const [estimating, setEstimating] = useState(false);
  const [tagsInput, setTagsInput] = useState("");
  const [assigned, setAssigned]   = useState<string[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{ personaId: string; reason: string }[]>([]);

  const estimate = async () => {
    if (!title.trim() || !desc.trim()) { alert("Preencha título e descrição."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setEstimating(true);
    try {
      const r = await estimateComplexityAI({
        apiKey: settings.anthropicKey, title, description: desc, targetDepts: depts
      });
      setComplexity(r.complexity as TaskComplexity);
      setComplexityReason(r.reason);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setEstimating(false);
    }
  };

  const toggleDept = (id: string) =>
    setDepts(d => d.includes(id) ? d.filter(x => x !== id) : [...d, id]);
  const toggleAssign = (id: string) =>
    setAssigned(a => a.includes(id) ? a.filter(x => x !== id) : [...a, id]);

  const availableAgents = PERSONAS.filter(p => {
    const emp = employees[p.id];
    if (!emp || emp.status !== "hired") return false;
    if (depts.length === 0) return true;
    const [d] = p.slotId.split(":");
    return depts.includes(d);
  });

  const autoDispatch = async () => {
    if (!title.trim() || !desc.trim()) { alert("Preencha título e descrição primeiro."); return; }
    if (!settings.anthropicKey) { alert("Configure a chave Anthropic em ⚙️."); return; }
    const hiredIds = Object.values(employees).filter(e => e.status === "hired").map(e => e.personaId);
    if (hiredIds.length === 0) { alert("Sem agentes contratados. Vai no RH primeiro."); return; }
    setSuggestLoading(true);
    try {
      const sug = await suggestAgentsAI({
        apiKey: settings.anthropicKey,
        model:  settings.model,
        title, description: desc, targetDepts: depts, hiredAgentIds: hiredIds
      });
      setSuggestions(sug);
      // pré-marca os sugeridos
      setAssigned(prev => Array.from(new Set([...prev, ...sug.map(s => s.personaId)])));
    } catch (e) {
      alert("Falha no auto-dispatch: " + (e as Error).message);
    } finally {
      setSuggestLoading(false);
    }
  };

  const submit = () => {
    if (!title.trim()) { alert("Título obrigatório."); return; }
    if (depts.length === 0) { alert("Escolha ao menos 1 departamento-alvo."); return; }
    const tags = tagsInput.split(",").map(s => s.trim()).filter(Boolean);
    create({
      title: title.trim(),
      description: desc.trim(),
      clientId: clientId || undefined,
      targetDepts: depts,
      assignedTo: assigned,
      priority,
      complexity,
      tags,
      status: "pending"
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-3xl max-h-[90vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="font-mono text-xs text-gold">NEW-01</div>
            <h2 className="font-display font-black uppercase text-3xl leading-none">NOVA DEMANDA</h2>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone">fechar ×</button>
        </header>

        <div className="p-5 space-y-4">
          <div>
            <div className="tag text-graydk mb-1">título da demanda</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="ex: VSL de lançamento Mentoria VPC — versão 3"
              className="w-full border border-ink bg-bone px-3 py-2 font-display font-bold text-lg uppercase"
            />
          </div>
          <div>
            <div className="tag text-graydk mb-1">cliente</div>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[12px]">
              <option value="">— interno / sem cliente —</option>
              {CLIENTS.map(c => (
                <option key={c.id} value={c.id}>
                  {c.shortName} ({c.segment})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="tag text-graydk mb-1">descrição / briefing</div>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={4}
              placeholder="contexto, objetivo, restrições, prazo…"
              className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] resize-y"
            />
          </div>

          <div>
            <div className="tag text-graydk mb-2">departamentos-alvo (clique para adicionar)</div>
            <div className="flex flex-wrap gap-2">
              {DEPARTMENTS.map(d => (
                <button
                  key={d.id}
                  onClick={() => toggleDept(d.id)}
                  className={`btn ${depts.includes(d.id) ? "bg-ink text-bone border-ink" : "btn-fire"}`}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>

          <div className="border-2 border-red/30 bg-red/5 p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-bold uppercase text-sm">🧠 complexidade da demanda</div>
                <div className="font-crimson italic text-paper text-sm">
                  roteia pra IA certa — trivial/low usa Haiku, medium usa Sonnet, high/critical usa Opus
                </div>
              </div>
              <button onClick={estimate} disabled={estimating}
                      className="btn bg-ink text-bone border-ink disabled:opacity-50">
                {estimating ? "estimando…" : "🧠 estimar com IA"}
              </button>
            </div>
            <div className="flex gap-1 flex-wrap">
              {(["trivial","low","medium","high","critical"] as TaskComplexity[]).map(c => (
                <button key={c} onClick={() => setComplexity(c)}
                  className={`btn ${complexity === c
                    ? c === "trivial" ? "bg-graymd text-ink border-ink"
                    : c === "low"      ? "bg-graydk text-bone border-ink"
                    : c === "medium"   ? "bg-ink text-bone border-ink"
                    : c === "high"     ? "bg-gold text-ink border-gold"
                    : "bg-red text-bone border-red"
                    : "btn-fire"}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="tag text-graydk mt-2">
              modelo escolhido: <span className="font-mono normal-case">{modelForComplexity(complexity, settings.model)}</span>
            </div>
            {complexityReason && (
              <div className="font-crimson italic text-paper text-sm mt-1">
                ↳ {complexityReason}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="tag text-graydk mb-2">prioridade</div>
              <div className="flex gap-1">
                {(["low","normal","high","urgent"] as TaskPriority[]).map(p => (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`btn ${priority === p ? `${PRIORITY_COLOR[p]} text-bone border-ink` : "btn-fire"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="tag text-graydk mb-2">tags (separadas por vírgula)</div>
              <input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="vsl, lançamento, headline"
                className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[12px]"
              />
            </div>
          </div>

          <div className="border-2 border-gold/40 bg-gold/10 p-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-bold uppercase text-sm">🤖 auto-dispatch</div>
                <div className="font-crimson italic text-paper text-sm">
                  deixa o Claude ler o briefing e sugerir quem do time deve pegar
                </div>
              </div>
              <button onClick={autoDispatch} disabled={suggestLoading}
                      className="btn bg-ink text-bone border-ink disabled:opacity-50">
                {suggestLoading ? "analisando…" : "✦ sugerir agentes"}
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="space-y-1 mt-2">
                {suggestions.map(s => {
                  const p = PERSONAS.find(x => x.id === s.personaId);
                  if (!p) return null;
                  return (
                    <div key={s.personaId} className="bg-bone border border-ink p-2 text-[12px]">
                      <span className="font-display font-bold uppercase">{p.name}</span>
                      <span className="font-crimson italic text-paper"> — {s.reason}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <div className="tag text-graydk mb-2">
              designar agentes específicos (opcional — {availableAgents.length} disponíveis
              {depts.length > 0 ? ` nos depts selecionados` : ""})
            </div>
            <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto border border-ink p-2">
              {availableAgents.map(p => {
                const checked = assigned.includes(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => toggleAssign(p.id)}
                    className={`btn ${checked ? "bg-gold border-gold text-ink" : "btn-fire"}`}
                  >
                    {p.name}
                  </button>
                );
              })}
              {availableAgents.length === 0 && (
                <span className="font-crimson italic text-paper text-sm">
                  Nenhum contratado{depts.length > 0 ? " nesses departamentos" : ""}. Contrate no RH primeiro.
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-ink/30">
            <button onClick={onClose} className="btn btn-fire">cancelar</button>
            <button onClick={submit} className="btn btn-hire">▸ criar demanda</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- DETAIL MODAL -------------------- */
function DemandDetailModal({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const task   = useTasks(s => s.tasks.find(t => t.id === taskId));
  const update = useTasks(s => s.update);
  const setStatus = useTasks(s => s.setStatus);
  const addOutput = useTasks(s => s.addOutput);
  const settings  = useSettings();

  const [outputBy, setOutputBy] = useState("");
  const [outputText, setOutputText] = useState("");
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoLog, setAutoLog] = useState<string[]>([]);
  const [mediating, setMediating] = useState(false);
  const [martelando, setMartelando] = useState(false);
  const [showDeck, setShowDeck] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const setStage          = useTasks(s => s.setStage);
  const addApproval       = useTasks(s => s.addApproval);
  const requestPresentation = useTasks(s => s.requestPresentation);
  const saveMeeting       = useUI.getState().saveMeeting;
  const setView           = useUI(s => s.setView);
  const [genBy, setGenBy]   = useState("");
  const [genKind, setGenKind] = useState<"image" | "video">("image");
  const [genPrompt, setGenPrompt] = useState("");
  const [genAspect, setGenAspect] = useState<"1:1" | "9:16" | "16:9" | "4:5">("1:1");
  const [genProvider, setGenProvider] = useState<"gemini" | "openai">("gemini");
  const [genLoading, setGenLoading] = useState(false);
  const [improveLoading, setImproveLoading] = useState(false);

  const improvePrompt = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    const baseText = genPrompt.trim() || task.title + " — " + task.description;
    if (!baseText) { alert("Escreva um prompt cru ou preencha briefing primeiro."); return; }
    const cli = task.clientId ? clientById(task.clientId) : undefined;
    const ov  = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
    const eff = cli ? effectiveBrand(cli, ov) : undefined;
    setImproveLoading(true);
    try {
      const better = await improveImagePromptAI({
        apiKey: settings.anthropicKey,
        rawPrompt: baseText,
        format: `${genKind} ${genAspect}`,
        aspect: genAspect,
        brandPalette: eff ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent } : undefined,
        clientContext: cli ? `${cli.name} (${cli.segment}). ${eff?.audience ?? ""}` : undefined,
        spokespersonRef: !!eff?.spokespersonUrl,
        specialist: genKind === "video" ? "azev300" : "ruben"
      });
      setGenPrompt(better);
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setImproveLoading(false);
    }
  };

  if (!task) return null;

  const buildClientContext = () => {
    const cli = task.clientId ? clientById(task.clientId) : undefined;
    const ov  = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
    const eff = cli ? effectiveBrand(cli, ov) : undefined;
    return cli && eff
      ? `${cli.name} (${cli.segment}). Posicionamento: ${cli.pitch}. Público: ${eff.audience}.` +
        (eff.persona ? ` Persona: ${eff.persona}.` : "") +
        (eff.voiceTone ? ` Tom: ${eff.voiceTone}.` : "")
      : undefined;
  };

  const textOutputs = () => task.outputs
    .filter(o => (o.kind ?? "text") === "text")
    .map(o => {
      const p = PERSONAS.find(x => x.id === o.by);
      return { by: p?.name ?? o.by, text: o.text };
    });

  const mediar = async () => {
    const outs = textOutputs();
    if (outs.length < 2) { alert("Conciliação exige pelo menos 2 outputs em texto."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    setMediating(true);
    try {
      const synth = await mediateConflictAI({
        apiKey: settings.anthropicKey, model: settings.model,
        topic: task.title, outputs: outs, clientContext: buildClientContext()
      });
      addOutput(task.id, "marcio", synth, { kind: "text", provider: "anthropic", model: "marcio-consigliere" });
    } catch (e) { alert("Falha: " + (e as Error).message); }
    finally { setMediating(false); }
  };

  const voto_minerva = async () => {
    const outs = textOutputs();
    if (outs.length < 1) { alert("Precisa de pelo menos 1 output pra Trump bater o martelo."); return; }
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    if (!confirm("⚖️ Convocar voto de Minerva — Trump bate o martelo. Decisão final, irreversível pelo time. Confirma?")) return;
    setMartelando(true);
    try {
      const marcioOut = task.outputs.find(o => o.by === "marcio");
      const verdict = await tiebreakerAI({
        apiKey: settings.anthropicKey, model: "claude-opus-4-7",
        topic: task.title, outputs: outs,
        marcioMediation: marcioOut?.text,
        clientContext: buildClientContext()
      });
      addOutput(task.id, "trump", verdict, { kind: "text", provider: "anthropic", model: "trump-tiebreaker" });
      addApproval(task.id, {
        stage: "client-approval", by: "trump", decision: "approved",
        notes: "Voto de Minerva — bate-martelo do Trump"
      });
    } catch (e) { alert("Falha: " + (e as Error).message); }
    finally { setMartelando(false); }
  };

  const runAutoExec = async () => {
    if (!settings.anthropicKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    const assigned = task.assignedTo
      .map(id => PERSONAS.find(p => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p));
    if (assigned.length === 0) { alert("Designe ao menos 1 agente antes de auto-executar."); return; }

    setAutoLoading(true);
    setAutoLog([`▸ ${assigned.length} agente${assigned.length === 1 ? "" : "s"} convocado${assigned.length === 1 ? "" : "s"}`]);

    const cli = task.clientId ? clientById(task.clientId) : undefined;
    const ov  = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
    const eff = cli ? effectiveBrand(cli, ov) : undefined;
    const clientContext = cli && eff
      ? `${cli.name} (${cli.segment}). Posicionamento: ${cli.pitch}. Público: ${eff.audience}.` +
        (eff.persona ? ` Persona: ${eff.persona}.` : "") +
        (eff.voiceTone ? ` Tom de voz da marca: ${eff.voiceTone}.` : "")
      : undefined;
    const routedModel = modelForComplexity(task.complexity, settings.model);

    const employeesState = useRH.getState().employees;
    for (const p of assigned) {
      const prof = profileOf(p.id, p.tier);
      const customPrompt = employeesState[p.id]?.systemPromptOverride;
      try {
        setAutoLog(l => [...l, `▸ ${p.name} (${routedModel}${customPrompt ? " · prompt custom" : ""}) trabalhando…`]);
        const text = await autoExecuteByAgentAI({
          apiKey: settings.anthropicKey,
          model: routedModel,
          agentName: p.name,
          agentVoice: prof.voice,
          agentSkills: prof.skills,
          agentExpectations: prof.expectations,
          taskTitle: task.title,
          taskDescription: task.description,
          clientContext,
          systemPromptOverride: customPrompt
        });
        addOutput(task.id, p.id, text, { kind: "text", provider: "anthropic", model: routedModel });
        setAutoLog(l => [...l, `   ✓ ${p.name} entregou`]);
      } catch (e) {
        setAutoLog(l => [...l, `   ✗ ${p.name} falhou: ${(e as Error).message}`]);
      }
    }
    if (task.status === "pending") setStatus(task.id, "review");
    setAutoLoading(false);
    setAutoLog(l => [...l, "▸ execução concluída · status → EM REVISÃO"]);
  };

  // candidatos por capacidade
  const candidates = (task.assignedTo.length > 0 ? task.assignedTo : PERSONAS.map(p => p.id))
    .map(id => PERSONAS.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const generate = async () => {
    if (!genBy) { alert("Selecione o agente."); return; }
    const p = PERSONAS.find(x => x.id === genBy);
    if (!p) return;
    const prof = profileOf(p.id, p.tier);
    const cli = task.clientId ? clientById(task.clientId) : undefined;
    const ov = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
    const eff = cli ? effectiveBrand(cli, ov) : undefined;
    const refUrl = eff?.spokespersonUrl ?? eff?.logoUrl;
    const clientCtx = cli && eff
      ? ` CLIENTE: ${cli.name} (${cli.segment}). Posicionamento: ${cli.pitch}. ` +
        `Público-alvo: ${eff.audience}.` +
        (eff.persona ? ` Persona ideal: ${eff.persona}.` : "") +
        ` Paleta: primário ${eff.primary}, secundário ${eff.secondary}, acento ${eff.accent}.` +
        ` Tipografia: ${eff.font ?? "default"}.` +
        (eff.voiceTone ? ` Tom de voz: ${eff.voiceTone}.` : "") +
        (eff.brief ? ` Briefing geral: ${eff.brief}.` : "") +
        ` Tags da marca: ${cli.tags.join(", ")}.` +
        (eff.spokespersonUrl
          ? " O garoto/garota-propaganda oficial foi enviado como imagem de referência — preserve o rosto e o estilo."
          : eff.logoUrl
            ? " O logotipo oficial foi enviado como referência — preserve a identidade visual."
            : "")
      : "";
    // se user não passou prompt, monta um cinema-grade automático
    const autoPrompt = genKind === "image"
      ? creativeAdPrompt({
          taskBrief: task.title + " — " + task.description,
          agentVoice: prof.voice,
          format: `${genKind} ${genAspect}`,
          aspect: genAspect,
          brand: eff
            ? { primary: eff.primary, secondary: eff.secondary, accent: eff.accent, font: eff.font }
            : {},
          spokespersonUrl: eff?.spokespersonUrl,
          audience: eff?.audience
        })
      : `Cinema-quality short ${genKind} (${genAspect}) for: ${task.title}. ${task.description}. ` +
        `Aesthetic: ${prof.voice}. Skills lens: ${prof.skills.join(", ")}.${clientCtx}`;
    const finalPrompt = genPrompt.trim() || autoPrompt;

    setGenLoading(true);
    try {
      if (genKind === "image") {
        const key   = genProvider === "gemini" ? settings.geminiKey : settings.openaiKey;
        const model = genProvider === "gemini" ? settings.geminiImageModel : settings.openaiImageModel;
        if (!key) { alert(`Configure a chave ${genProvider} em ⚙️.`); return; }
        const fn = genProvider === "gemini" ? generateImageGemini : generateImageOpenAI;
        const out = await fn({
          apiKey: key, model, prompt: finalPrompt, aspect: genAspect,
          referenceUrl: refUrl
        });
        addOutput(task.id, genBy, finalPrompt, {
          kind: "image", mediaUrl: out.dataUrl, provider: out.provider, model: out.model
        });
      } else {
        if (!settings.higgsfieldKey) { alert("Configure a chave Higgsfield em ⚙️."); return; }
        const aspect = genAspect === "1:1" || genAspect === "9:16" || genAspect === "16:9" ? genAspect : "9:16";
        const out = await generateVideoHiggsfield({
          apiKey: settings.higgsfieldKey,
          model: settings.higgsfieldModel,
          prompt: finalPrompt,
          aspect
        });
        addOutput(task.id, genBy, finalPrompt, {
          kind: "video", mediaUrl: out.url, provider: out.provider, model: out.model
        });
      }
      setGenPrompt("");
      if (task.status === "pending") setStatus(task.id, "in-progress");
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-3xl max-h-[90vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-4 flex items-start justify-between gap-3 sticky top-0 z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`tag px-2 py-1 ${STATUS_COLOR[task.status]}`}>
                {STATUS_LABEL[task.status]}
              </span>
              <span className={`inline-block w-3 h-3 ${PRIORITY_COLOR[task.priority]}`} />
              <span className="tag text-bone/70">prioridade {task.priority}</span>
            </div>
            <h2 className="font-display font-black uppercase text-2xl leading-tight mt-1">
              {task.title}
            </h2>
          </div>
          <div className="flex flex-col gap-1 shrink-0">
            <button onClick={() => setShowPipeline(true)}
              className="btn bg-red text-bone border-red text-base py-2 px-3">
              🤖 PIPELINE AUTO
            </button>
            <button onClick={runAutoExec} disabled={autoLoading}
              className="btn bg-gold text-ink border-gold disabled:opacity-50">
              {autoLoading ? "…" : "▶ só executar"}
            </button>
            <button onClick={onClose} className="btn bg-bone text-ink border-bone">fechar ×</button>
          </div>
        </header>

        {autoLog.length > 0 && (
          <div className="bg-ink/95 text-bone px-5 py-3 border-b border-gold">
            <div className="font-mono text-[11px] text-gold mb-1">AUTO-EXEC · LOG</div>
            <pre className="font-mono text-[12px] whitespace-pre-wrap leading-snug">
{autoLog.join("\n")}
            </pre>
          </div>
        )}

        <div className="p-5 space-y-4">
          <div>
            <div className="tag text-graydk mb-1">briefing</div>
            <textarea
              value={task.description}
              onChange={(e) => update(task.id, { description: e.target.value })}
              rows={3}
              className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] resize-y"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div>
              <div className="tag text-graydk mb-1">status</div>
              <select
                value={task.status}
                onChange={(e) => setStatus(task.id, e.target.value as TaskStatus)}
                className="border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
                {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div>
              <div className="tag text-graydk mb-1">deptos</div>
              <div className="flex gap-1">
                {task.targetDepts.map(d => (
                  <span key={d} className="tag bg-ink text-bone px-1.5">
                    {ALL_DEPTS.find(x => x.id === d)?.name ?? d}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ===== ESTÁGIO + APROVAÇÃO ===== */}
          <section className="border-2 border-ink p-3 bg-bone">
            <div className="font-display font-bold uppercase text-sm mb-2">▸ fluxo de aprovação</div>
            <div className="flex flex-wrap items-center gap-1 mb-3">
              {(["draft","delegated","in-progress","supervisor-review","client-approval","delivered"] as TaskStage[]).map(st => (
                <span key={st}
                      className={`tag px-1.5 ${task.stage === st ? STAGE_COLOR[st] : "bg-bone border border-ink/30 text-graydk"}`}>
                  {STAGE_LABEL[st]}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
              {task.stage !== "in-progress" && (
                <button onClick={() => setStage(task.id, "in-progress")} className="btn btn-fire">▶ executar</button>
              )}
              {(task.stage === "in-progress" || task.stage === "delegated") && (
                <button
                  onClick={() => addApproval(task.id, { stage: "supervisor-review", by: "marcio", decision: "approved" })}
                  className="btn bg-gold text-ink border-gold">→ supervisor revisa</button>
              )}
              {task.stage === "supervisor-review" && (
                <>
                  <button
                    onClick={() => addApproval(task.id, { stage: "supervisor-review", by: "supervisor", decision: "approved" })}
                    className="btn bg-ink text-bone border-ink">✓ supervisor aprovou</button>
                  <button
                    onClick={() => {
                      const note = prompt("Motivo da revisão?") ?? "";
                      addApproval(task.id, { stage: "supervisor-review", by: "supervisor", decision: "needs-revision", notes: note });
                      setStage(task.id, "in-progress");
                    }}
                    className="btn btn-fire">↺ pede revisão</button>
                </>
              )}
              {task.stage === "client-approval" && (
                <>
                  <button
                    onClick={() => addApproval(task.id, { stage: "client-approval", by: "marcio", decision: "approved" })}
                    className="btn bg-ink text-bone border-ink">✓ Marcio aprovou</button>
                  <button
                    onClick={() => {
                      const note = prompt("Motivo?") ?? "";
                      addApproval(task.id, { stage: "client-approval", by: "marcio", decision: "rejected", notes: note });
                    }}
                    className="btn bg-red text-bone border-red">✗ rejeitar</button>
                </>
              )}
              {task.stage === "delivered" && (
                <span className="tag bg-ink text-bone px-2 py-1.5">✓ entregue</span>
              )}
              {(task.stage === "delivered" || task.stage === "client-approval") && (
                <button
                  onClick={() => {
                    const opts = DEPARTMENTS.map(d => d.name).join(", ");
                    const ans = prompt(`Passar bastão pra qual depto?\n\nOpções: ${opts}\n\n(separe por vírgula se quiser múltiplos)`);
                    if (!ans) return;
                    const targets = ans.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
                    const matched = DEPARTMENTS
                      .filter(d => targets.some(t => d.name.toLowerCase().includes(t) || d.id === t))
                      .map(d => d.id);
                    if (matched.length === 0) { alert("Nenhum depto reconhecido."); return; }
                    const lastOutputs = task.outputs.slice(-3).map(o => {
                      const p = PERSONAS.find(x => x.id === o.by);
                      return `▸ ${p?.name ?? o.by}:\n${o.text.slice(0, 800)}`;
                    }).join("\n\n");
                    const newId = useTasks.getState().create({
                      title: `[handoff] ${task.title}`,
                      description:
                        `Continuação da demanda "${task.title}".\n\n` +
                        `Entregas anteriores:\n${lastOutputs}\n\n` +
                        `Próxima etapa: executar a partir do que veio antes.`,
                      clientId: task.clientId,
                      targetDepts: matched,
                      assignedTo: [],
                      priority: task.priority,
                      complexity: task.complexity,
                      stage: "delegated",
                      tags: [...task.tags, "handoff"],
                      status: "pending",
                      references: task.references,
                      handoffFrom: task.id
                    });
                    update(task.id, { handoffToDepts: matched });
                    alert(`Nova demanda criada (#${newId.slice(0,6)}) pros depts: ${matched.join(", ")}.`);
                  }}
                  className="btn bg-gold text-ink border-gold">→ passar pra outro depto</button>
              )}
              {task.outputs.filter(o => (o.kind ?? "text") === "text").length >= 2 && (
                <button onClick={mediar} disabled={mediating}
                  className="btn bg-paper text-bone border-paper disabled:opacity-50">
                  {mediating ? "conciliando…" : "🤝 conciliar (Marcio)"}
                </button>
              )}
              {task.outputs.filter(o => (o.kind ?? "text") === "text").length >= 1 && (
                <button onClick={voto_minerva} disabled={martelando}
                  className="btn bg-red text-bone border-red disabled:opacity-50">
                  {martelando ? "martelando…" : "⚖️ voto de Minerva (Trump)"}
                </button>
              )}
              <button
                onClick={() => {
                  requestPresentation(task.id);
                  saveMeeting({
                    title: `Apresentação · ${task.title}`,
                    topic: task.title,
                    mode: "ata",
                    picks: task.assignedTo,
                    agenda: [
                      `Apresentação da demanda: ${task.title}`,
                      `Estágio: ${STAGE_LABEL[task.stage ?? "draft"]}`,
                      "",
                      task.description,
                      "",
                      "▸ ENTREGAS",
                      ...task.outputs.map(o => `— ${o.by}: ${o.text.slice(0, 200)}…`)
                    ]
                  });
                  alert("Reunião de apresentação convocada — veja em 'Reuniões salvas'.");
                  setView("meetings");
                }}
                className="btn btn-fire">🪑 convocar reunião</button>
            </div>

            {(task.approvals ?? []).length > 0 && (
              <div className="mt-3 border-t border-ink/20 pt-2">
                <div className="tag text-graydk mb-1">histórico</div>
                <div className="space-y-1">
                  {(task.approvals ?? []).slice().reverse().map(a => (
                    <div key={a.id} className="font-mono text-[11px] flex items-center gap-2">
                      <span className={`tag px-1 ${a.decision === "approved" ? "bg-ink text-bone"
                        : a.decision === "rejected" ? "bg-red text-bone"
                        : "bg-gold text-ink"}`}>{a.decision}</span>
                      <span className="text-graydk">{a.by}</span>
                      <span>· {STAGE_LABEL[a.stage]}</span>
                      <span className="text-graydk ml-auto">{fmt(a.at)}</span>
                      {a.notes && <span className="italic text-paper truncate">— {a.notes}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ===== REFERÊNCIAS ===== */}
          {(task.references ?? []).length > 0 && (
            <section className="border-2 border-ink p-3 bg-bone">
              <div className="tag text-graydk mb-2">referências ({task.references!.length})</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {task.references!.map(r => (
                  <a key={r.id} href={r.url} target="_blank" rel="noreferrer"
                     className="border border-ink p-1 bg-bone block hover:bg-ink/5">
                    {r.kind === "image" && r.url ? (
                      <img src={r.url} alt="" className="w-full h-20 object-cover" />
                    ) : (
                      <div className="w-full h-20 bg-ink text-bone flex items-center justify-center text-2xl">
                        {r.kind === "audio" ? "♪" : r.kind === "video" ? "▶" : r.kind === "file" ? "▤" : "↗"}
                      </div>
                    )}
                    <div className="text-[10px] font-mono truncate p-1">{r.name ?? r.url.slice(0, 40)}</div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {task.assignedTo.length > 0 && (
            <div>
              <div className="tag text-graydk mb-2">designados</div>
              <div className="flex flex-wrap gap-2">
                {task.assignedTo.map(id => {
                  const p = PERSONAS.find(x => x.id === id);
                  if (!p) return null;
                  const prof = profileOf(p.id, "especialista");
                  return (
                    <div key={id} className="border border-ink p-2 bg-bone flex items-start gap-2 max-w-xs">
                      <div className="shrink-0 w-9 h-9 flex items-center justify-center font-display font-black text-sm"
                           style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
                        {initials(p.name)}
                      </div>
                      <div>
                        <div className="font-display font-bold uppercase text-sm">{p.name}</div>
                        <div className="tag text-graydk">{prof.skills.slice(0, 2).join(" · ")}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <div className="tag text-graydk mb-2">entregas / outputs</div>
            {task.outputs.length === 0 && (
              <div className="border border-graymd p-4 text-center font-crimson italic text-graydk text-sm">
                Sem entregas registradas ainda.
              </div>
            )}
            <div className="space-y-2">
              {task.outputs.map(o => {
                const p = PERSONAS.find(x => x.id === o.by);
                return (
                  <div key={o.id} className="border border-ink bg-bone p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-display font-bold uppercase text-sm flex items-center gap-2">
                        {o.kind === "image" && <span className="tag bg-gold text-ink px-1.5">IMG</span>}
                        {o.kind === "video" && <span className="tag bg-red text-bone px-1.5">VID</span>}
                        {p?.name ?? o.by}
                        {o.provider && <span className="tag text-graydk normal-case">· {o.provider}/{o.model}</span>}
                      </span>
                      <span className="tag text-graydk">{fmt(o.at)}</span>
                    </div>
                    {o.kind === "image" && o.mediaUrl && (
                      <div className="my-2 border border-ink/30">
                        <img src={o.mediaUrl} alt="" className="max-w-full block" />
                        <div className="flex justify-end p-1">
                          <a href={o.mediaUrl} download={`output-${o.id}.png`}
                             className="tag underline">baixar</a>
                        </div>
                      </div>
                    )}
                    {o.kind === "video" && o.mediaUrl && (
                      <div className="my-2 border border-ink/30">
                        <video src={o.mediaUrl} controls className="max-w-full block" />
                        <div className="flex justify-end p-1">
                          <a href={o.mediaUrl} target="_blank" rel="noreferrer"
                             className="tag underline">abrir</a>
                        </div>
                      </div>
                    )}
                    {(o.kind ?? "text") === "text" ? (
                      <div className="bg-bone border border-ink/10 p-2 max-h-[60vh] overflow-y-auto">
                        <MarkdownView text={o.text} />
                      </div>
                    ) : (
                      <pre className="font-mono text-[11px] whitespace-pre-wrap leading-relaxed text-paper">
{o.text}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ===== GERAÇÃO DE APRESENTAÇÃO ===== */}
            <div className="mt-4">
              {!showDeck ? (
                <button onClick={() => setShowDeck(true)}
                  className="w-full btn bg-red text-bone border-red py-3 text-base">
                  📊 gerar APRESENTAÇÃO visual (slides + imagens + .pptx)
                </button>
              ) : (
                <DeckGenerator task={task} onSaved={() => setShowDeck(false)} />
              )}
            </div>

            {/* ===== GERAÇÃO IA ===== */}
            <div className="mt-4 border-2 border-ink p-3 bg-bone">
              <div className="font-display font-bold uppercase text-sm mb-2">▸ gerar criativo isolado (imagem ou vídeo)</div>
              <div className="grid md:grid-cols-2 gap-2 mb-2">
                <div>
                  <div className="tag text-graydk mb-1">agente designado</div>
                  <select value={genBy} onChange={(e) => setGenBy(e.target.value)}
                          className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
                    <option value="">selecione…</option>
                    {candidates.map(p => {
                      const caps = capabilitiesOf(p.slotId, profileOf(p.id, p.tier).skills);
                      return (
                        <option key={p.id} value={p.id}>
                          {p.name}  · {caps.join("/")}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <div className="tag text-graydk mb-1">tipo</div>
                  <div className="flex border border-ink">
                    {(["image","video"] as const).map(k => (
                      <button key={k} onClick={() => setGenKind(k)}
                              className={`tag flex-1 py-1.5 border-r last:border-r-0 border-ink
                                ${genKind === k ? "bg-ink text-bone" : "bg-bone text-ink"}`}>
                        {k === "image" ? "🎨 imagem" : "🎬 vídeo"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-2 mb-2">
                <div>
                  <div className="tag text-graydk mb-1">proporção</div>
                  <div className="flex border border-ink">
                    {(["1:1","9:16","16:9","4:5"] as const).map(a => (
                      <button key={a} onClick={() => setGenAspect(a)}
                              className={`tag flex-1 py-1.5 border-r last:border-r-0 border-ink
                                ${genAspect === a ? "bg-gold text-ink" : "bg-bone"}`}>
                        {a}
                      </button>
                    ))}
                  </div>
                </div>
                {genKind === "image" && (
                  <div>
                    <div className="tag text-graydk mb-1">provedor</div>
                    <div className="flex border border-ink">
                      {(["gemini","openai"] as const).map(p => (
                        <button key={p} onClick={() => setGenProvider(p)}
                                className={`tag flex-1 py-1.5 border-r last:border-r-0 border-ink
                                  ${genProvider === p ? "bg-red text-bone" : "bg-bone"}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {genKind === "video" && (
                  <div>
                    <div className="tag text-graydk mb-1">provedor</div>
                    <div className="flex border border-ink">
                      <span className="tag flex-1 py-1.5 bg-red text-bone text-center">higgsfield</span>
                    </div>
                  </div>
                )}
              </div>
              <textarea
                value={genPrompt}
                onChange={(e) => setGenPrompt(e.target.value)}
                rows={4}
                placeholder="prompt customizado (vazio = template cinema-grade automático a partir do briefing + voz do agente + brand kit)…"
                className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px] resize-y mb-2"
              />
              <div className="flex justify-between gap-2">
                <button onClick={improvePrompt} disabled={improveLoading}
                  className="btn btn-fire disabled:opacity-50"
                  title="Claude reescreve seu prompt em padrão cinema/editorial">
                  {improveLoading ? "🪄 melhorando…" : "🪄 melhorar prompt com IA"}
                </button>
                <button onClick={generate} disabled={genLoading}
                        className="btn bg-ink text-bone border-ink disabled:opacity-50">
                  {genLoading ? "gerando…" : (genKind === "image" ? "✦ gerar imagem" : "✦ gerar vídeo")}
                </button>
              </div>
              <p className="font-crimson italic text-graydk text-[11px] mt-2">
                {genKind === "video"
                  ? "vídeo demora 30s–2min · poll automático até concluir"
                  : "imagem ~5-15s · resultado salvo direto na demanda"}
              </p>
            </div>
            {/* novo output manual */}
            <div className="mt-3 border-t border-ink/30 pt-3">
              <div className="tag text-graydk mb-1">registrar entrega manual</div>
              <div className="flex gap-2 items-start">
                <select
                  value={outputBy}
                  onChange={(e) => setOutputBy(e.target.value)}
                  className="border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
                  <option value="">selecione agente…</option>
                  {(task.assignedTo.length > 0 ? task.assignedTo : PERSONAS.map(p => p.id))
                    .map(id => PERSONAS.find(p => p.id === id))
                    .filter(Boolean)
                    .map(p => <option key={p!.id} value={p!.id}>{p!.name}</option>)}
                </select>
                <textarea
                  value={outputText}
                  onChange={(e) => setOutputText(e.target.value)}
                  rows={2}
                  placeholder="texto da entrega…"
                  className="flex-1 border border-ink bg-bone px-2 py-1 font-mono text-[12px] resize-y"
                />
                <button
                  onClick={() => {
                    if (!outputBy || !outputText.trim()) return;
                    addOutput(task.id, outputBy, outputText.trim());
                    setOutputText("");
                  }}
                  className="btn btn-hire">↳ registrar</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPipeline && <PipelineRunner taskId={task.id} onClose={() => setShowPipeline(false)} />}
    </div>
  );
}
